import { StandardEvents } from '@shopify/events';

/**
 * Zengen — Mengenrabatt-Staffel (1x/2x/3x) auf Produktseiten.
 *
 * Ersetzt für getaggte Produkte den Standard-Mengenwähler durch eine
 * vertikale Radio-Auswahl. Der hier angezeigte Preis ist eine reine
 * Vorschau (Liquid-Berechnung); maßgeblich für den tatsächlich
 * verrechneten Preis bleibt die Rabatt-Konfiguration im Shopify-Backend
 * (Discount), nicht dieses Skript.
 *
 * Bei einem Variantenwechsel rendert das Theme die Seite neu im Hintergrund
 * und meldet das über das productSelect-Event. Daraus übernehmen wir die
 * frisch berechneten Preise, damit die Staffel zur gewählten Größe passt.
 */
class QuantityTierSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @type {Element | null} */
  #section = null;

  /** @param {Event} event */
  #handleChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;

    const quantityInput = this.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = radio.value;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Übernimmt die Preise der neu gewählten Variante aus dem frisch gerenderten HTML.
   * Radios und Mengen-Input bleiben unangetastet, damit die gewählte Menge erhalten bleibt.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const next = html.querySelector(`quantity-tier-selector[data-block-id="${this.dataset.blockId}"]`);
        if (!next) return;

        const currentPrices = this.querySelectorAll('.quantity-tier-selector__price');
        const nextPrices = next.querySelectorAll('.quantity-tier-selector__price');
        if (currentPrices.length !== nextPrices.length) return;

        nextPrices.forEach((price, index) => {
          currentPrices[index].innerHTML = price.innerHTML;
        });

        if (next.dataset.variantId) this.dataset.variantId = next.dataset.variantId;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quantity-tier-selector] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('quantity-tier-selector')) {
  customElements.define('quantity-tier-selector', QuantityTierSelector);
}
